import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useDocumentMeta from '../../utils/useDocumentMeta';
import TermsModal from '../../components/Modals/TermsModal/TermsModal';
import PublicNavbar from '../../components/PublicNavbar/PublicNavbar';
import './LandingPage.css';

const ClipboardIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
);

const CheckIcon = () => (
    <svg viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
    </svg>
);

const NewLandingPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [showTermsModal, setShowTermsModal] = useState(false);
    useDocumentMeta('seo-home-title', 'seo-home-desc', { canonicalPath: '/' });

    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'DietistPro',
        url: 'https://dietistpro.com',
        applicationCategory: 'HealthApplication',
        operatingSystem: 'Web',
        description: t('seo-home-desc'),
        offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'EUR',
            description: t('seo-jsonld-offer'),
        },
    };

    return (
        <>
        <PublicNavbar />
        <div className="nlp">
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
            />

            {/* ── HERO ── */}
            <header className="nlp-hero" id="hero">
                <div className="nlp-container">
                    <div className="nlp-hero-text">
                        <h1 className="nlp-hero-title">
                            {t('nlp-hero-title-prefix')}<span>{t('nlp-hero-title-highlight')}</span>{t('nlp-hero-title-suffix')}
                        </h1>
                        <p className="nlp-hero-subtitle">
                            {t('nlp-hero-subtitle')}
                        </p>
                        <div className="nlp-hero-actions">
                            <button className="nlp-btn-hero-primary" onClick={() => navigate('/register')}>{t('nlp-hero-cta-primary')}</button>
                            {/* <button className="nlp-btn-hero-secondary">{t('nlp-hero-cta-secondary')}</button> */}
                        </div>
                    </div>
                    <div className="nlp-hero-visual">
                        <div className="nlp-hero-mockup">
                            <img src="/images/LandingPage/charts.png" alt="DietistPro Home Dashboard" />
                        </div>
                        <div className="nlp-hero-blob-1" />
                        <div className="nlp-hero-blob-2" />
                    </div>
                </div>
            </header>

            {/* ── FEATURE 1 – AI Diet Planning ── */}
            <section className="nlp-section-white" id="features">
                <div className="nlp-container">
                    <div className="nlp-feature-grid reverse">
                        <div className="nlp-feature-content">
                            <span className="nlp-badge nlp-badge-orange">{t('nlp-f1-badge')}</span>
                            <h2 className="nlp-section-title">{t('nlp-f1-title')}</h2>
                            <p className="nlp-section-body">
                                {t('nlp-f1-body')}
                            </p>
                            <ul className="nlp-checklist">
                                <li><CheckIcon /> {t('nlp-f1-check-1')}</li>
                                <li><CheckIcon /> {t('nlp-f1-check-2')}</li>
                            </ul>
                        </div>
                        <div className="nlp-feature-visual">
                            <div className="nlp-screenshot">
                                <img src="/images/LandingPage/first_section.png" alt="Diet generation interface" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── FEATURE 2 – Client Management ── */}
            <section className="nlp-section-gray" id="dashboard">
                <div className="nlp-container">
                    <div className="nlp-feature-grid">
                        <div className="nlp-feature-visual">
                            <div className="nlp-screenshot">
                                <img src="/images/LandingPage/second-section.png" alt="Client management dashboard" />
                            </div>
                        </div>
                        <div className="nlp-feature-content">
                            <span className="nlp-badge nlp-badge-green">{t('nlp-f2-badge')}</span>
                            <h2 className="nlp-section-title">{t('nlp-f2-title')}</h2>
                            <p className="nlp-section-body">
                                {t('nlp-f2-body')}
                            </p>
                            <div className="nlp-mini-cards">
                                <div className="nlp-mini-card">
                                    <div className="nlp-mini-card-title">{t('nlp-f2-card-1-title')}</div>
                                    <p>{t('nlp-f2-card-1-body')}</p>
                                </div>
                                <div className="nlp-mini-card">
                                    <div className="nlp-mini-card-title">{t('nlp-f2-card-2-title')}</div>
                                    <p>{t('nlp-f2-card-2-body')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── FEATURE 3 – Scheduling ── */}
            <section className="nlp-section-white">
                <div className="nlp-container">
                    <div className="nlp-feature-grid reverse">
                        <div className="nlp-feature-content">
                            <span className="nlp-badge nlp-badge-blue">{t('nlp-f3-badge')}</span>
                            <h2 className="nlp-section-title">{t('nlp-f3-title')}</h2>
                            <p className="nlp-section-body">
                                {t('nlp-f3-body')}
                            </p>
                            <div className="nlp-quote-block">
                                <p>"{t('nlp-f3-quote')}"</p>
                            </div>
                        </div>
                        <div className="nlp-feature-visual">
                            <div className="nlp-screenshot">
                                <img src="/images/LandingPage/third-section.png" alt="Integrated practice calendar" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── FEATURE 4 – Data Analytics ── */}
            <section className="nlp-section-gray">
                <div className="nlp-container">
                    <div className="nlp-feature-grid">
                        <div className="nlp-feature-visual">
                            <div className="nlp-screenshot">
                                <img src="/images/LandingPage/fourth-section.png" alt="Health charts and progress tracking" />
                            </div>
                        </div>
                        <div className="nlp-feature-content">
                            <span className="nlp-badge nlp-badge-orange">{t('nlp-f4-badge')}</span>
                            <h2 className="nlp-section-title">{t('nlp-f4-title')}</h2>
                            <p className="nlp-section-body">
                                {t('nlp-f4-body')}
                            </p>
                            <div className="nlp-steps">
                                <div className="nlp-step">
                                    <div className="nlp-step-number">1</div>
                                    <div>
                                        <div className="nlp-step-title">{t('nlp-f4-step-1-title')}</div>
                                        <div className="nlp-step-body">{t('nlp-f4-step-1-body')}</div>
                                    </div>
                                </div>
                                <div className="nlp-step">
                                    <div className="nlp-step-number">2</div>
                                    <div>
                                        <div className="nlp-step-title">{t('nlp-f4-step-2-title')}</div>
                                        <div className="nlp-step-body">{t('nlp-f4-step-2-body')}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="nlp-cta-section" id="pricing">
                <div className="nlp-container">
                    <div className="nlp-cta-card">
                        <div className="nlp-cta-blob-1" />
                        <div className="nlp-cta-blob-2" />
                        <div className="nlp-cta-content">
                            <h2 className="nlp-cta-title">{t('nlp-cta-title')}</h2>
                            <p className="nlp-cta-subtitle">
                                {t('nlp-cta-subtitle')}
                            </p>
                            <button className="nlp-btn-cta" onClick={() => navigate('/register')}>{t('nlp-cta-btn')}</button>
                            <p className="nlp-cta-note">{t('nlp-cta-note')}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── FOOTER ── */}
            <footer className="nlp-footer">
                <div className="nlp-container">
                    <div className="nlp-footer-inner">
                        <div className="nlp-footer-logo">
                            <div className="nlp-footer-logo-icon"><ClipboardIcon /></div>
                            <span className="nlp-footer-logo-text">Dietist<span>Pro</span></span>
                        </div>
                        <div className="nlp-footer-links">
                            <a href="#" onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}>{t('nlp-footer-privacy')}</a>
                            <a href="#" onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}>{t('nlp-footer-terms')}</a>
                        </div>
                        <span className="nlp-footer-copy">{t('nlp-footer-copy')}</span>
                    </div>
                </div>
            </footer>

        </div>

        <TermsModal open={showTermsModal} onClose={() => setShowTermsModal(false)} />
        </>
    );
};

export default NewLandingPage;
